import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE, getToken, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/StatusBanner";
import { fmtDate } from "@/lib/format";
import { canDo } from "@/lib/auth";
import { toast } from "sonner";
import { Camera, CheckCircle2, XCircle, Eye, Download } from "lucide-react";
import { ProtectedImage } from "@/components/ProtectedImage";
import { openProtectedFile, downloadProtectedFile } from "@/lib/protectedFile";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [method, setMethod] = useState("cash");
  const [driverOrderNo, setDriverOrderNo] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingReg, setUploadingReg] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [photoTs, setPhotoTs] = useState(Date.now()); // for cache-busting
  const [regPhotoTs, setRegPhotoTs] = useState(Date.now());
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [showReverse, setShowReverse] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [reversing, setReversing] = useState(false);
  const fileInputRef = useRef(null);
  const regFileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error(formatError(e, "Order not found"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load the linked invoice (if any) — only meaningful on financial-plan
  // tenants when the order is completed and attached to a customer.
  useEffect(() => {
    if (!order) return;
    if (!tenant?.feature_flags?.financial) return;
    if (order.status !== "completed") return;
    if (!order.customer_account_id) return;
    api.get(`/finance/orders/${id}/linked-invoice`)
      .then(({ data }) => setLinkedInvoice(data.invoice))
      .catch(() => setLinkedInvoice(null));
  }, [order, tenant, id]);

  const reverseOrder = async () => {
    if (reverseReason.trim().length < 3) return toast.error("Reason too short");
    if (reversing) return;  // extra guard against double-submit
    setReversing(true);
    try {
      const { data } = await api.post(`/finance/orders/${id}/reverse`,
                                       { reason: reverseReason });
      if (data.already_reversed) {
        toast.info(`Order was already reversed — credit note ${data.credit_note_no}`);
      } else {
        toast.success(`Order reversed. Credit note ${data.credit_note_no} issued.`);
      }
      // Redirect immediately so the user cannot re-submit from the stale page.
      // Financial-plan tenants have History enabled; navigate there.
      navigate("/app/history");
    } catch (e) {
      toast.error(formatError(e, "Reversal failed"));
      setReversing(false);
    }
    // NOTE: no setReversing(false) on success path — we're navigating away.
  };

  const complete = async () => {
    if (!order.receipt_photo_file_id) {
      toast.error("Please upload/take a receipt photo before completing the order.");
      return;
    }
    if (method === "order" && !driverOrderNo.trim()) {
      toast.error("Please enter the driver/customer order number.");
      return;
    }
    setCompleting(true);
    try {
      const { data } = await api.post(`/orders/${id}/complete`, {
        completion_method: method,
        driver_order_number: driverOrderNo.trim() || null,
        completion_notes: notes || null,
      });
      setOrder(data);
      toast.success("Order completed and moved to History");
    } catch (e) {
      toast.error(formatError(e, "Failed to complete order"));
    } finally { setCompleting(false); }
  };

  const cancel = async () => {
    if (cancelReason.trim().length < 3) return toast.error("Reason too short");
    try {
      const { data } = await api.post(`/orders/${id}/cancel`, { reason: cancelReason });
      setOrder(data);
      setShowCancel(false);
      toast.success("Order cancelled");
    } catch (e) { toast.error(formatError(e, "Failed to cancel")); }
  };

  const onPhotoPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/orders/${id}/receipt-photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw { response: { data: err } };
      }
      const data = await res.json();
      setOrder({ ...order, receipt_photo_file_id: data.file_id });
      setPhotoTs(Date.now());
      toast.success("Receipt photo uploaded");
    } catch (err) {
      toast.error(formatError(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onRegPhotoPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingReg(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/orders/${id}/registration-photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw { response: { data: err } };
      }
      const data = await res.json();
      setOrder({ ...order, registration_photo_file_id: data.file_id });
      setRegPhotoTs(Date.now());
      toast.success("Registration photo uploaded");
    } catch (err) {
      toast.error(formatError(err, "Upload failed"));
    } finally {
      setUploadingReg(false);
      if (regFileInputRef.current) regFileInputRef.current.value = "";
    }
  };

  const openPdf = () => {
    fetch(`${API_BASE}/orders/${id}/receipt.pdf`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };

  if (loading || !order) return <div className="label-overline text-slate-500">Loading…</div>;

  const canCancel = canDo(user.role, "order.cancel") && !["completed", "cancelled"].includes(order.status);
  const canComplete = canDo(user.role, "order.complete") && !["completed", "cancelled"].includes(order.status);
  const photoPath = order.receipt_photo_file_id
    ? `/files/${order.receipt_photo_file_id}?_=${photoTs}`
    : null;
  const regPhotoPath = order.registration_photo_file_id
    ? `/files/${order.registration_photo_file_id}?_=${regPhotoTs}`
    : null;
  const receiptFilename = `receipt-${order.order_no}.jpg`;
  const regFilename = `registration-${order.order_no}.jpg`;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 label-overline text-slate-500">
        <Link to="/app/open-orders">Orders</Link><span>/</span>
        <span>{order.order_no}</span>
      </div>
      <div className="flex items-center gap-4 mt-2 mb-6 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Order {order.order_no}</h1>
        <StatusPill status={order.status} testId="order-status" />
        {order.finance_status === "reversed" && (
          <StatusPill status="reversed" testId="order-finance-status"/>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 text-sm">
            <div className="label-overline">Registration</div>
            <div className="font-mono font-bold text-lg text-blue-700">{order.registration_no}</div>
            <div className="label-overline">Service</div>
            <div>{order.service_type_snapshot?.label}</div>
            <div className="label-overline">Customer</div>
            <div>
              {order.customer_snapshot ? (
                <span className="font-mono">{order.customer_snapshot.customer_code} · {order.customer_snapshot.display_name}</span>
              ) : (
                <span className="text-amber-700">
                  Walk-in{order.walkin_snapshot?.driver_name ? ` — ${order.walkin_snapshot.driver_name}` : ""}
                  {order.walkin_snapshot?.company_name ? ` (${order.walkin_snapshot.company_name})` : ""}
                </span>
              )}
            </div>
            <div className="label-overline">Entry time</div>
            <div className="font-mono text-xs">{fmtDate(order.opened_at)}</div>
            {order.completed_at && (<>
              <div className="label-overline">Completed</div>
              <div className="font-mono text-xs">{fmtDate(order.completed_at)}</div>
              <div className="label-overline">Method</div>
              <div className="font-mono">{order.completion_method || "—"}</div>
              {order.driver_order_number && (<>
                <div className="label-overline">Driver Order #</div>
                <div className="font-mono">{order.driver_order_number}</div>
              </>)}
            </>)}
            {order.cancelled_at && (<>
              <div className="label-overline">Cancelled</div>
              <div className="font-mono text-xs">{fmtDate(order.cancelled_at)} — {order.cancel_reason}</div>
            </>)}
            {order.notes && (<>
              <div className="label-overline">Entry notes</div>
              <div className="whitespace-pre-wrap">{order.notes}</div>
            </>)}
            {order.completion_notes && (<>
              <div className="label-overline">Completion notes</div>
              <div className="whitespace-pre-wrap">{order.completion_notes}</div>
            </>)}
          </div>

          {/* Completion section */}
          {canComplete && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="label-overline mb-3">Complete order</div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { v: "cash", label: "Cash" },
                  { v: "card", label: "Card / Swipe" },
                  { v: "order", label: "Order" },
                ].map((o) => (
                  <button key={o.v}
                          type="button"
                          data-testid={`complete-method-${o.v}`}
                          onClick={() => setMethod(o.v)}
                          className={`border rounded py-3 text-sm font-semibold transition
                            ${method === o.v
                              ? "border-blue-700 bg-blue-50 text-blue-800"
                              : "border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                    {o.label}
                  </button>
                ))}
              </div>

              {method === "order" && (
                <div className="mb-3">
                  <Label className="label-overline">Driver / Customer Order Number *</Label>
                  <Input data-testid="complete-driver-order"
                         className="rounded h-10 mt-1 font-mono"
                         placeholder="e.g. PO-1024"
                         value={driverOrderNo} onChange={(e) => setDriverOrderNo(e.target.value)} />
                </div>
              )}

              <div className="mb-3">
                <Label className="label-overline">Completion notes (optional)</Label>
                <Textarea data-testid="complete-notes" rows={2} className="rounded mt-1"
                          value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button data-testid="order-complete"
                        onClick={complete} disabled={completing}
                        className="rounded bg-green-600 hover:bg-green-700 flex items-center gap-2">
                  <CheckCircle2 size={16} /> {completing ? "Completing…" : "Complete Order"}
                </Button>
                {canCancel && (
                  <Button data-testid="order-cancel-open" variant="outline"
                          className="rounded border-red-400 text-red-600 hover:bg-red-50 flex items-center gap-2"
                          onClick={() => setShowCancel(true)}>
                    <XCircle size={16} /> Cancel order…
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Finance Flow P0 — Reverse invoiced order (financial + owner/admin) */}
          {order.status === "completed" && tenant?.feature_flags?.financial &&
           canDo(user.role, "order.cancel") && order.finance_status === "reversed" && (
            <div className="mt-4 border border-amber-300 bg-amber-50 p-4 rounded"
                 data-testid="order-reversed-info">
              <div className="label-overline text-amber-800">Order Reversed</div>
              <div className="mt-2 text-sm space-y-1">
                <div><b>Credit note:</b> <span className="font-mono">{order.reversal_credit_note_no || "—"}</span></div>
                <div><b>Reversed:</b> <span className="font-mono text-xs">{fmtDate(order.reversed_at)}</span></div>
                <div><b>Reason:</b> <span className="italic">{order.reversal_reason}</span></div>
              </div>
              <div className="mt-2 text-[11px] text-amber-800">
                Original invoice remains in history unchanged. The credit note reduces the customer's balance.
              </div>
            </div>
          )}

          {order.status === "completed" && tenant?.feature_flags?.financial &&
           canDo(user.role, "order.cancel") && order.finance_status !== "reversed" &&
           linkedInvoice && (
            <div className="mt-4 border border-slate-300 bg-slate-50 p-4 rounded"
                 data-testid="reverse-invoiced-order-block">
              <div className="label-overline">Linked Invoice</div>
              <div className="mt-1 text-sm">
                <span className="font-mono font-bold">{linkedInvoice.invoice_no}</span>
                {" · "}
                <span className="text-slate-600">status {linkedInvoice.status}</span>
              </div>
              {linkedInvoice.status === "issued" && !showReverse && (
                <Button data-testid="reverse-order-open"
                        variant="outline"
                        className="mt-3 rounded border-red-400 text-red-600 hover:bg-red-50 flex items-center gap-2"
                        onClick={() => setShowReverse(true)}>
                  <XCircle size={16}/> Reverse this invoiced order
                </Button>
              )}
              {showReverse && (
                <div className="mt-3 border border-red-300 bg-red-50 p-3 rounded">
                  <div className="text-xs text-red-800 mb-2">
                    A credit note will be issued against invoice{" "}
                    <b>{linkedInvoice.invoice_no}</b> for the full order amount.
                    The original invoice stays in history unchanged.
                  </div>
                  <div className="label-overline text-red-700">Reason (required)</div>
                  <textarea data-testid="reverse-order-reason"
                            className="w-full mt-2 border border-red-300 rounded p-2 h-20 text-sm"
                            value={reverseReason}
                            onChange={(e) => setReverseReason(e.target.value)}
                            disabled={reversing} />
                  <div className="mt-2 flex gap-2">
                    <Button data-testid="reverse-order-confirm"
                            onClick={reverseOrder} disabled={reversing}
                            className="rounded bg-red-600 hover:bg-red-700">
                      {reversing ? "Reversing…" : "Issue credit note"}
                    </Button>
                    <Button variant="outline" className="rounded"
                            disabled={reversing}
                            onClick={() => { setShowReverse(false); setReverseReason(""); }}>
                      Back
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showCancel && (
            <div className="mt-4 border border-red-300 bg-red-50 p-4 rounded">
              <div className="label-overline text-red-700">Cancel reason (required)</div>
              <textarea data-testid="order-cancel-reason"
                        className="w-full mt-2 border border-red-300 rounded p-2 h-20"
                        value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              <div className="mt-2 flex gap-2">
                <Button data-testid="order-cancel-confirm"
                        className="rounded bg-red-600 hover:bg-red-700" onClick={cancel}>Confirm cancel</Button>
                <Button variant="outline" className="rounded" onClick={() => setShowCancel(false)}>Back</Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Registration photo (Stage 2.5) */}
          <div className="bg-white border border-slate-200 rounded p-5">
            <div className="label-overline mb-3">Registration Photo</div>
            <p className="text-xs text-slate-600 mb-3">
              Evidence of the number plate captured at entry.
            </p>
            {regPhotoPath ? (
              <div className="border border-slate-200 rounded overflow-hidden mb-3">
                <ProtectedImage path={regPhotoPath} alt="Registration"
                                testId="order-reg-photo-img"
                                className="w-full h-auto max-h-40 object-contain bg-slate-50" />
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-300 rounded p-4 text-center text-slate-500 text-xs mb-3">
                No registration photo attached
              </div>
            )}
            {regPhotoPath && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Button variant="outline" data-testid="order-reg-photo-view"
                        onClick={() => openProtectedFile(regPhotoPath,
                          "Could not open registration photo")}
                        className="rounded flex items-center justify-center gap-2">
                  <Eye size={14}/> View
                </Button>
                <Button variant="outline" data-testid="order-reg-photo-download"
                        onClick={() => downloadProtectedFile(regPhotoPath, regFilename,
                          "Could not download registration photo")}
                        className="rounded flex items-center justify-center gap-2">
                  <Download size={14}/> Download
                </Button>
              </div>
            )}
            <input ref={regFileInputRef} type="file" accept="image/*" capture="environment"
                   className="hidden"
                   data-testid="order-reg-photo-input"
                   onChange={onRegPhotoPicked} />
            <Button data-testid="order-reg-photo-btn"
                    onClick={() => regFileInputRef.current?.click()}
                    disabled={uploadingReg || order.status === "cancelled"}
                    variant="outline"
                    className="rounded w-full flex items-center justify-center gap-2">
              <Camera size={14} /> {uploadingReg ? "Uploading…" : (regPhotoPath ? "Replace" : "Add photo")}
            </Button>
          </div>

          {/* Receipt photo — SIGNED PROOF */}
          <div className="bg-white border border-slate-200 rounded p-5"
               data-testid="order-signed-receipt-section">
            <div className="label-overline mb-3">Signed Receipt Photo</div>
            <p className="text-xs text-slate-600 mb-3">
              Photo of the paper receipt signed by the driver at completion.
              This is proof of service for the customer.
            </p>
            {photoPath ? (
              <div className="border border-slate-200 rounded overflow-hidden mb-3">
                <ProtectedImage path={photoPath} alt="Signed receipt"
                                testId="order-receipt-photo-img"
                                className="w-full h-auto max-h-64 object-contain bg-slate-50" />
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-300 rounded p-6 text-center text-slate-500 text-sm mb-3">
                No signed receipt photo attached
              </div>
            )}
            {photoPath && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Button variant="outline" data-testid="order-receipt-photo-view"
                        onClick={() => openProtectedFile(photoPath,
                          "Could not open receipt photo")}
                        className="rounded flex items-center justify-center gap-2">
                  <Eye size={14}/> View
                </Button>
                <Button variant="outline" data-testid="order-receipt-photo-download"
                        onClick={() => downloadProtectedFile(photoPath, receiptFilename,
                          "Could not download receipt photo")}
                        className="rounded flex items-center justify-center gap-2">
                  <Download size={14}/> Download
                </Button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
                   className="hidden"
                   data-testid="order-receipt-photo-input"
                   onChange={onPhotoPicked} />
            <Button data-testid="order-receipt-photo-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || order.status === "cancelled"}
                    className="rounded w-full bg-slate-900 hover:bg-slate-800 flex items-center justify-center gap-2">
              <Camera size={16} /> {uploading ? "Uploading…" : (photoPath ? "Replace photo" : "Add photo")}
            </Button>
          </div>

          {/* Operational receipt PDF */}
          <div className="bg-white border border-slate-200 rounded p-5">
            <div className="label-overline mb-3">Operational Receipt</div>
            <p className="text-xs text-slate-600 mb-3">
              Driver slip / order receipt.
              <br/><b className="text-red-600">Not a tax invoice.</b>
            </p>
            <div className="flex flex-col gap-2">
              <a href={`${API_BASE}/orders/${id}/receipt.html`}
                 data-testid="order-receipt-html"
                 target="_blank" rel="noreferrer"
                 className="text-center w-full border border-slate-300 rounded py-2 hover:bg-slate-50 text-sm">
                View / Print HTML
              </a>
              <Button data-testid="order-receipt-pdf"
                      className="rounded bg-blue-700 hover:bg-blue-800"
                      onClick={openPdf}>Download PDF</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
